import React from "react"
import PropTypes from "prop-types"
import { ValueWithLabelTemplate } from "../../../components/layouts/containers/ValueWithLabelElements.container"
import IconWithTooltipAndCustomBody from "../../../components/molecules/IconWithTooltipAndCustomBody.molecule"
import {
  formatSprintDetailedEstimationData,
  getEmptyClientSprintDetailedEstimationData,
  isEstimationTypeBeta,
} from "../../../utils/clientReport.utils"
import Chip from "../../../components/atoms/misc/Chip.atom"
import Tooltip from "../../../components/atoms/Tooltip.atom"
import { numberFormatter } from "../../../utils/numbers.utils"
import useScreenDimensionsContext from "../../../hooks/contextConsumers/useScreenDimensionsContext.hook"

const DetailedEstimationLegend = ({
  className,
  data,
  loading = false,
  onMouseEnter,
  onMouseLeave,
}) => {
  const { screenWidth } = useScreenDimensionsContext()

  if (!data) {
    data = formatSprintDetailedEstimationData(
      getEmptyClientSprintDetailedEstimationData()
    )[0]?.data
  }
  const totalItem = data?.find((item) => item.estimationType === "TOTAL_POINTS")
  const restItems = data?.filter(
    (item) => item.estimationType !== "TOTAL_POINTS"
  )
  const uniqueElements = restItems.filter(
    (item, index, self) =>
      index === self.findIndex((obj) => obj.name === item.name) &&
      item.extraInfo?.label
  )
  const legendItems = uniqueElements?.map((item) => (
    <LegendItem
      key={item.name}
      item={item}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      loading={loading}
    />
  ))
  if (screenWidth < 764) {
    return (
      <div
        className={`custom-recharts-legend square-rounded-color-list-legend ${className} flex flex-wrap w-full justify-content-between align-items-baseline`}
      >
        {totalItem && (
          <LegendItem key={totalItem.name} item={totalItem} loading={loading} />
        )}
        {legendItems}
      </div>
    )
  }
  return (
    <div className="flex-1 flex w-full align-items-top flex-column sm:flex-row sm:justify-content-between gap-3">
      {totalItem && (
        <div className="square-rounded-color-list-legend total-points-item">
          <LegendItem
            key={totalItem.name}
            item={totalItem}
            containerClassName="align-items-top p-0"
            loading={loading}
          />
        </div>
      )}
      <div
        className={`custom-recharts-legend square-rounded-color-list-legend ${className} flex flex-wrap w-full justify-content-between align-items-baseline`}
      >
        {legendItems}
      </div>
    </div>
  )
}

const LegendItem = ({
  item,
  onMouseEnter,
  onMouseLeave,
  containerClassName = "align-items-center px-2 pb-2",
  loading = false,
}) => {
  return (
    <div
      className={`flex gap-2 color-list-legend-item ${containerClassName} ${loading ? "w-full" : "w-fit"}`}
      data-name={item.name}
      onMouseLeave={onMouseLeave}
      onMouseEnter={onMouseEnter}
    >
      <ValueWithLabelTemplate
        label={
          <div className="flex gap-2 align-items-center">
            {item.color && (
              <div className="color" style={{ background: item.color }} />
            )}
            {item.extraInfo?.label || item.name}
            {item.extraInfo?.tooltipConfig && (
              <IconWithTooltipAndCustomBody
                iconClassName="pi pi-info-circle text-lg"
                tooltipBody={item.extraInfo.tooltipConfig.tooltipValue}
                position={item.extraInfo.tooltipConfig.position}
              />
            )}
          </div>
        }
        value={
          <div className="flex gap-1 align-items-center">
            <span>
              {typeof item.value === "number" && item.value >= 0 ?
                `~${numberFormatter(item.value)} pts`
              : "-"}
            </span>
            {isEstimationTypeBeta(item.estimationType) && (
              <BetaEstimationChip estimationType={item.estimationType} />
            )}
          </div>
        }
        className="flex flex-column gap-1"
        loading={loading}
      />
    </div>
  )
}

const BetaEstimationChip = ({ estimationType }) => {
  return (
    <>
      <Chip
        className={`status-chip text-white flex align-items-center estimation-label-${estimationType}`}
        style={{
          backgroundColor: `var(--secondary-color-highlight)`,
        }}
        template={
          <span
            style={{
              fontSize: "12px",
              color: "var(--surface-b)",
            }}
          >
            Est
          </span>
        }
      />
      <Tooltip target={`.estimation-label-${estimationType}`}>
        Puntos preliminares sujetos a reestimación del equipo de desarrollo
        según el diseño final.
      </Tooltip>
    </>
  )
}

export default DetailedEstimationLegend

DetailedEstimationLegend.propTypes = {
  data: PropTypes.array,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
}
